import * as GoalActions from '@/store/actions/Goal.actions';
import * as GoalMutations from '@/store/mutations/Goal.mutations';
import * as AuthMutations from '@/store/mutations/Auth.mutations';
import * as DatabaseService from '@/services/Database.service';
import * as NotificationService from '@/services/Notification.service';
import * as notificationTypes from '@/constants/notificationTypes';
import { dictionaryFromDocumentArray, isArrayEmpty, isObjectEmpty } from '@/helpers/dataHelpers';

const getInitialState = () => ({
  isGoalLoading: false,
  isGoalWritingData: false,
  goals: {},
});

const state = getInitialState();

const getters = {
  goalIds(state) {
    return Object.keys(state.goals).map(key => key);
  },
  loggedInUserGoals(state, getters, rootState) {
    if (getters.goalIds.length === 0) {
      return {};
    }

    return getters.goalIds.reduce((acc, goalId) => {
      if (state.goals[goalId].userId === rootState.auth.loggedInUserId) {
        acc[goalId] = state.goals[goalId];
      }

      return acc;
    }, {});
  },
};

const mutations = {
  [GoalMutations.GOAL_REQUEST_DATA](state) {
    state.isGoalLoading = true;
  },
  [GoalMutations.GOAL_BEGIN_WRITE_DATA](state) {
    state.isGoalWritingData = true;
  },
  [GoalMutations.GOAL_COMPLETE_WRITE_DATA](state) {
    state.isGoalWritingData = false;
  },
  [GoalMutations.RECEIVE_GOALS](state, payload) {
    if (!isObjectEmpty(payload)) {
      state.goals = {
        ...state.goals,
        ...payload,
      };
    }
    state.isGoalLoading = false;
  },
  [AuthMutations.LOG_OUT](state) {
    Object.assign(state, getInitialState());
  },
};

const actions = {
  async [GoalActions.FETCH_GOAL_BY_ID](context, payload) {
    context.commit(GoalMutations.GOAL_REQUEST_DATA);
    try {
      const goalQueryResult = await DatabaseService.getGoalById(payload.goalId);
      if (goalQueryResult.exists) {
        const goalDocument = { [goalQueryResult.id]: { id: goalQueryResult.id, ...goalQueryResult.data() } };
        context.commit(GoalMutations.RECEIVE_GOALS, goalDocument);
      } else {
        context.commit(GoalMutations.RECEIVE_GOALS, {});
      }
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
    }
  },
  async [GoalActions.FETCH_GOALS_BY_USER_IDS](context, payload) {
    context.commit(GoalMutations.GOAL_REQUEST_DATA);
    try {
      const querySnapshots = await Promise.all(DatabaseService.getGoalsByUserIds(payload.userIds, payload.workspaceId));

      // Filter out any snapshots that returned empty arrays.
      const snapshotsWithResults = querySnapshots.filter(snapshot => !snapshot.empty);

      if (!isArrayEmpty(snapshotsWithResults)) {
        const goalDocuments = snapshotsWithResults.map(snapshot => snapshot.docs).flat();
        const shapedGoalDocuments = dictionaryFromDocumentArray(goalDocuments);

        context.commit(GoalMutations.RECEIVE_GOALS, shapedGoalDocuments);
      } else {
        context.commit(GoalMutations.RECEIVE_GOALS, {});
      }
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
    }
  },
  async [GoalActions.FETCH_GOALS_BY_USER_IDS_BY_DATES](context, payload) {
    context.commit(GoalMutations.GOAL_REQUEST_DATA);
    try {
      const querySnapshots = await Promise.all(
        DatabaseService.getGoalsByUserIdsByDates(payload.userIds, payload.dates, payload.workspaceId),
      ); // eslint-disable-line max-len

      // Filter out any snapshots that returned empty arrays.
      const snapshotsWithResults = querySnapshots.filter(snapshot => !snapshot.empty);

      if (!isArrayEmpty(snapshotsWithResults)) {
        const goalDocuments = snapshotsWithResults.map(snapshot => snapshot.docs).flat();
        const shapedGoalDocuments = dictionaryFromDocumentArray(goalDocuments);

        context.commit(GoalMutations.RECEIVE_GOALS, shapedGoalDocuments);
      } else {
        context.commit(GoalMutations.RECEIVE_GOALS, {});
      }
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
    }
  },
  async [GoalActions.CREATE_NEW_GOAL](context, payload) {
    context.commit(GoalMutations.GOAL_BEGIN_WRITE_DATA);
    try {
      const documentCreateConfirmation = await DatabaseService.addNewGoal(payload);
      if (documentCreateConfirmation) {
        context.commit(GoalMutations.GOAL_COMPLETE_WRITE_DATA);
        await context.dispatch(GoalActions.FETCH_GOAL_BY_ID, { goalId: documentCreateConfirmation.id });
        NotificationService.showNotification(notificationTypes.CREATE_GOAL_SUCCESS);
      } else {
        NotificationService.showNotification(notificationTypes.CREATE_GOAL_ERROR);
        throw new Error('There was an error creating goal');
      }
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
    }
  },
  async [GoalActions.UPDATE_GOAL](context, payload) {
    context.commit(GoalMutations.GOAL_BEGIN_WRITE_DATA);
    try {
      await DatabaseService.updateGoalById(payload.goalId, payload.updatedGoal);
      context.commit(GoalMutations.GOAL_COMPLETE_WRITE_DATA);
      await context.dispatch(GoalActions.FETCH_GOAL_BY_ID, { goalId: payload.goalId });
      NotificationService.showNotification(notificationTypes.EDIT_GOAL_SUCCESS);
    } catch (err) {
      NotificationService.showNotification(notificationTypes.EDIT_GOAL_ERROR);
      console.error(err); // eslint-disable-line no-console
    }
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
